import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';

import { getDocumentName, getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { Admin as AdminCmp } from '../../components/Admin';
import { GenericAdminForm } from '../../forms/GenericAdmin';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ThirdPartiesCmp_SomePeople_QueryDocument } from '../../components/ThirdParties/queries/some-people.query';
import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { useMemos } from '../../forms/MemoBeforeSubmit/hooks/useMemos.hook';
import type { GenericAdminFormValues } from '../../forms/GenericAdmin';

import { PersonPage_OnePerson_QueryDocument } from './hooks/useFindOnePersonQuery.hook';

export const PersonPage_Admin_Person_Fragment = graphql(`
  fragment PersonPage_Admin_Person_Fragment on Person {
    createdAt
    creatorId
    _id
    isInternal
    statusId
    updatedAt
    updaterId
  }
`);

export const PersonPage_Admin_Person_Foapu_Mutation = graphql(`
  mutation PersonPage_Admin_Person_Foapu(
    $filter: IdFilterInput!
    $update: PersonPartialInput!
    $versionData: VersionDataInput
  ) {
    findOneAndPartialUpdatePerson(
      filter: $filter
      update: $update
      versionData: $versionData
    ) {
      _id
    }
  }
`);

interface Props {
  readonly paths: Readonly<{
    consult: string;
    edit: string;
  }>;
  readonly person: FragmentType<typeof PersonPage_Admin_Person_Fragment>;
}

export const Admin = ({ paths, person: pPerson }: Readonly<Props>) => {
  const { genericStatusesCodeMap } = useReferencesContext();

  const person = getFragmentData(PersonPage_Admin_Person_Fragment, pPerson);

  const { pathname: currentPath } = useLocation();
  const navigate = useNavigate();

  const isInEditionMode = currentPath === paths.edit;
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { enqueueSnackbar } = useSnackbar();

  const { clearMemos, memos, setMemos } = useMemos();

  // mutations
  const { mutate: foapuPersonMutation } = useGraphQLMutation(
    PersonPage_Admin_Person_Foapu_Mutation,
  );

  // on success
  const queryClient = useQueryClient();

  const mutate = useCallback(
    (
      values: GenericAdminFormValues,
      handleError: (error: unknown) => unknown,
      handleSuccess: () => unknown,
    ) => {
      const onSuccess = () => {
        leaveEditionMode();
        clearMemos();
        enqueueSnackbar('Modification enregistrée.');
        handleSuccess();
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(PersonPage_OnePerson_QueryDocument),
            { id: person._id },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(ThirdPartiesCmp_SomePeople_QueryDocument),
            {
              statusId: genericStatusesCodeMap.Disabled._id,
              isInternal: person.isInternal,
            },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(ThirdPartiesCmp_SomePeople_QueryDocument),
            {
              statusId: genericStatusesCodeMap.Enabled._id,
              isInternal: person.isInternal,
            },
          ],
        });
      };

      foapuPersonMutation(
        {
          filter: {
            _id: person._id,
          },
          update: values,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: handleError,
          onSuccess,
        },
      );
    },
    [
      clearMemos,
      enqueueSnackbar,
      foapuPersonMutation,
      genericStatusesCodeMap.Disabled._id,
      genericStatusesCodeMap.Enabled._id,
      leaveEditionMode,
      memos,
      person._id,
      person.isInternal,
      queryClient,
    ],
  );

  return (
    <>
      <AdminCmp
        createdAt={person.createdAt}
        creatorId={person.creatorId}
        id={person._id}
        statusId={person.statusId}
        updatedAt={person.updatedAt}
        updaterId={person.updaterId}
      />
      <SpeedDial
        actions={[
          {
            id: 'personAdmin',
            icon: <DisplaySettingsIcon />,
            name: 'Administrer',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur l'Administration de la Personne"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <GenericAdminForm
          clearMemos={clearMemos}
          memos={memos}
          onCancel={leaveEditionMode}
          onSubmit={mutate}
          setMemos={setMemos}
          statusId={person.statusId as GenericStatusId}
        />
      </RightDrawer>
    </>
  );
};
